/**
 * Adapted from Hermes CLI `_handle_prompt_compose_command`: edit a one-shot
 * next user turn, then explicitly submit it. Native editors become a shared
 * dialog; no shell command or system-prompt mutation is involved.
 * Copyright (c) 2025 Nous Research
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { useEffect, useRef, useState } from "react";
import { Button, Dialog, TextArea, stackStyle } from "@dayvise/react-ui";
import type { SendMessageOutcome } from "@dayvise/client-sdk";

export interface ComposedPrompt {
  readonly text: string;
  readonly interpretSlash: false;
  readonly idempotencyKey: string;
  readonly signal: AbortSignal;
}

interface EditorSession {
  readonly seed: string;
  readonly generation: number;
  readonly key: number;
}

/** Account and branch fencing applies to stale event handlers as well as renders. */
export function usePromptComposer(
  identity: string,
  send: (input: ComposedPrompt) => Promise<SendMessageOutcome>,
  onSent: (outcome: SendMessageOutcome) => void,
  onCancel: (seed: string) => void,
) {
  const current = useRef({ identity, generation: 0, request: null as AbortController | null });
  const attempt = useRef<{ text: string; key: string; generation: number } | null>(null);
  if (current.current.identity !== identity) {
    current.current.request?.abort();
    current.current = { identity, generation: current.current.generation + 1, request: null };
    attempt.current = null;
  }
  const [editor, setEditor] = useState<EditorSession | null>(null);
  const serial = useRef(0);
  const callbacks = useRef({ send, onSent, onCancel });
  callbacks.current = { send, onSent, onCancel };
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; current.current.request?.abort(); };
  }, []);
  useEffect(() => { setEditor(null); }, [identity]);
  const active = editor !== null && editor.generation === current.current.generation;
  const generation = current.current.generation;
  const isCurrent = (entry: EditorSession) => mounted.current && entry.generation === current.current.generation && entry.key === serial.current;

  function composePrompt(seed: string) {
    if (!mounted.current || generation !== current.current.generation) return;
    current.current.request?.abort();
    setEditor({ seed, generation: current.current.generation, key: ++serial.current });
  }

  const promptEditor = active ? (
    <PromptEditor
      key={editor.key}
      seed={editor.seed}
      onCancel={() => {
        if (!isCurrent(editor)) return;
        current.current.request?.abort();
        ++serial.current;
        setEditor(null);
        callbacks.current.onCancel(editor.seed);
      }}
      onSend={async (text) => {
        if (!isCurrent(editor)) return;
        // Retain the key after failure or cancellation: the server may already
        // have accepted this prose even when its reply never reached the editor.
        if (attempt.current?.text !== text || attempt.current.generation !== editor.generation) {
          attempt.current = { text, key: crypto.randomUUID(), generation: editor.generation };
        }
        const request = new AbortController();
        current.current.request = request;
        const outcome = await callbacks.current.send({ text, interpretSlash: false, idempotencyKey: attempt.current.key, signal: request.signal });
        if (request.signal.aborted || !isCurrent(editor)) return;
        if (!outcome.result) throw new Error("Send is not confirmed yet. Retry to recover the same message.");
        current.current.request = null;
        attempt.current = null;
        ++serial.current;
        setEditor(null);
        callbacks.current.onSent(outcome);
      }}
    />
  ) : null;
  return { composePrompt, promptEditor };
}

function PromptEditor({ seed, onCancel, onSend }: {
  readonly seed: string;
  readonly onCancel: () => void;
  readonly onSend: (text: string) => Promise<void>;
}) {
  const [text, setText] = useState(seed);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitting = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  async function submit() {
    if (submitting.current || !text.trim()) return;
    submitting.current = true;
    setPending(true);
    setError(null);
    try { await onSend(text); }
    catch (failure) { if (mounted.current) setError(failure instanceof Error ? failure.message : "Prompt could not be sent."); }
    finally { submitting.current = false; if (mounted.current) setPending(false); }
  }
  return (
    <div onKeyDownCapture={(event) => {
      if (event.key !== "Tab") return;
      const panel = event.currentTarget.querySelector('[role="dialog"]');
      const controls = panel?.querySelectorAll<HTMLElement>('button:not([disabled]), textarea:not([disabled]), [tabindex="0"]');
      const first = controls?.[0], last = controls?.[controls.length - 1];
      const focused = document.activeElement;
      if (!first || !last) return;
      if (event.shiftKey && (focused === first || !panel?.contains(focused))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (focused === last || !panel?.contains(focused))) {
        event.preventDefault(); first.focus();
      }
    }}>
    <Dialog open title="Compose prompt" description="Edit the next message for this conversation." onClose={onCancel}
      footer={<><Button variant="ghost" onClick={onCancel}>Cancel</Button><Button disabled={pending || !text.trim()} loading={pending} onClick={() => void submit()}>Send prompt</Button></>}>
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        <TextArea label="Prompt" value={text} onChange={(event) => setText(event.target.value)} disabled={pending} rows={12} />
        {error === null ? null : <p role="alert">{error}</p>}
      </div>
    </Dialog>
    </div>
  );
}
